import { createContext, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletKitchen } from '@/components/LinkToMenu/useOutletKitchen';
import useValidateQueryParams from '@/hooks/useValidateQueryParams';
import { setI18nCountry } from '@/i18n';

interface CurrencyContextType {
  currency: string;
  currencySymbol: string;
  country: string | undefined;
  isLoading: boolean;
}

const CurrencyContext = createContext<CurrencyContextType | null>(null);

interface CurrencyProviderProps {
  children: React.ReactNode;
}

export const CurrencyProvider = ({ children }: CurrencyProviderProps) => {
  const { queryParams } = useValidateQueryParams();
  const { outletId } = queryParams();
  const { currency, country, isLoading, data } = useOutletKitchen(outletId);
  const { t } = useTranslation();

  const apiCountry = country || data?.data?.country;

  useEffect(() => {
    setI18nCountry(apiCountry);
  }, [apiCountry]);

  const currencySymbol = useMemo(() => {
    if (isLoading) return '';

    const apiCurrency = currency || data?.data?.currency;
    const apiCountry = country || data?.data?.country;

    if (!apiCurrency) {
      const fallbackCurrency = apiCountry === 'SA' ? 'SAR' : 'AED';
      return t(`currency.${fallbackCurrency}`) || fallbackCurrency;
    }
    return t(`currency.${apiCurrency}`) || apiCurrency;
  }, [currency, isLoading, country, data, t]);

  const contextValue: CurrencyContextType = useMemo(() => {
    const apiCurrency = currency || data?.data?.currency;
    const apiCountry = country || data?.data?.country;
    const fallbackCurrency = apiCountry === 'SA' ? 'SAR' : 'AED';
    return {
      currency: apiCurrency || fallbackCurrency,
      currencySymbol,
      country: apiCountry,
      isLoading,
    };
  }, [currency, currencySymbol, country, data, isLoading]);

  return <CurrencyContext.Provider value={contextValue}>{children}</CurrencyContext.Provider>;
};

export const useCurrency = (): CurrencyContextType => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};
